/**
 * ChessBoard React Component
 * 
 * Renders an interactive chess board with pieces, legal move highlighting,
 * and responsive design for both desktop and mobile devices.
 * 
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ChessEngine } from '../ChessEngine';
import { Position, ChessPiece, Move, PlayerColor, positionsEqual } from '../types';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { PromotionDialog } from './PromotionDialog';
import './ChessBoard.css';

export interface ChessBoardProps {
  engine: ChessEngine;
  orientation?: PlayerColor;
  interactive?: boolean;
  showLegalMoves?: boolean;
  showLastMove?: boolean;
  showCoordinates?: boolean;
  isAIThinking?: boolean;
  onMove?: (move: Move) => void;
  onGameEnd?: (result: { winner?: PlayerColor; isDraw: boolean; reason: string }) => void;
  moveCount?: number; // Optional prop to trigger re-renders
}

interface ChessBoardState {
  selectedPiece?: Position;
  legalMoves: Position[];
  lastMove?: Move;
  highlightedSquares: Position[];
  animatingMove?: {
    from: Position;
    to: Position;
    piece: ChessPiece;
    capturedPiece?: ChessPiece;
  };
  captureAnimation?: {
    position: Position;
    piece: ChessPiece;
  };
  promotionPending?: {
    from: Position;
    to: Position;
  };
}

/**
 * ChessBoard component renders the chess board and handles user interactions
 */
export const ChessBoard: React.FC<ChessBoardProps> = ({
  engine,
  orientation = 'white',
  interactive = true,
  showLegalMoves = true,
  showLastMove = true,
  showCoordinates = true,
  isAIThinking = false,
  onMove,
  onGameEnd,
  moveCount // Accept moveCount prop
}) => {
  const [state, setState] = useState<ChessBoardState>({
    legalMoves: [],
    highlightedSquares: []
  });

  const boardRef = useRef<HTMLDivElement>(null);
  const [draggedPiece, setDraggedPiece] = useState<{ piece: ChessPiece; from: Position } | null>(null);
  const [touchState, setTouchState] = useState<{
    startPosition?: Position;
    currentPosition?: Position;
    isDragging: boolean;
  }>({ isDragging: false });

  // Get current game state
  const gameState = engine.getGameState();
  const board = gameState.board;
  const currentPlayer = gameState.currentPlayer;
  const gameStatus = gameState.gameStatus;

  // Update last move when move history changes
  useEffect(() => {
    const moveHistory = engine.getMoveHistory();
    if (moveHistory.length > 0) {
      setState(prev => ({
        ...prev,
        lastMove: moveHistory[moveHistory.length - 1]
      }));
    }
  }, [engine, gameState.moveHistory.length]);

  // Check for game end
  useEffect(() => {
    if (gameStatus.type !== 'active' && gameStatus.type !== 'check' && onGameEnd) {
      let result: { winner?: PlayerColor; isDraw: boolean; reason: string };
      
      if (gameStatus.type === 'checkmate') {
        result = {
          winner: gameStatus.winner,
          isDraw: false,
          reason: 'Checkmate'
        };
      } else if (gameStatus.type === 'stalemate') {
        result = {
          isDraw: true,
          reason: 'Stalemate'
        };
      } else if (gameStatus.type === 'draw') {
        result = {
          isDraw: true,
          reason: gameStatus.reason
        };
      } else if (gameStatus.type === 'resignation') {
        result = {
          winner: gameStatus.winner,
          isDraw: false,
          reason: 'Resignation'
        };
      } else if (gameStatus.type === 'timeout') {
        result = {
          winner: gameStatus.winner,
          isDraw: false,
          reason: 'Timeout'
        };
      } else {
        return;
      }
      
      onGameEnd(result);
    }
  }, [gameStatus, onGameEnd]);

  /**
   * Handles square click for piece selection and movement
   */
  const handleSquareClick = useCallback((position: Position) => {
    if (!interactive) return;

    const piece = board[position.rank][position.file];

    // If a piece is already selected
    if (state.selectedPiece) {
      // Check if clicking on a legal move destination
      const isLegalMove = state.legalMoves.some(move => positionsEqual(move, position));
      
      if (isLegalMove) {
        // Execute the move
        executeMove(state.selectedPiece, position);
      } else if (piece && piece.color === currentPlayer) {
        // Select a different piece of the same color
        selectPiece(position);
      } else {
        // Deselect
        setState(prev => ({
          ...prev,
          selectedPiece: undefined,
          legalMoves: [],
          highlightedSquares: []
        }));
      }
    } else {
      // No piece selected - try to select one
      if (piece && piece.color === currentPlayer) {
        selectPiece(position);
      }
    }
  }, [interactive, board, currentPlayer, state.selectedPiece, state.legalMoves]);

  /**
   * Selects a piece and highlights its legal moves
   */
  const selectPiece = useCallback((position: Position) => {
    const legalMoves = engine.getLegalMoves(position);
    
    setState(prev => ({
      ...prev,
      selectedPiece: position,
      legalMoves,
      highlightedSquares: [position, ...legalMoves]
    }));
  }, [engine]);

  /**
   * Executes a move from one position to another with animation
   */
  const executeMove = useCallback((from: Position, to: Position) => {
    const piece = board[from.rank][from.file];
    const capturedPiece = board[to.rank][to.file];
    
    // Check if pawn promotion is needed
    if (piece && piece.type === 'pawn') {
      const promotionRank = piece.color === 'white' ? 7 : 0;
      if (to.rank === promotionRank) {
        // Show promotion dialog
        setState(prev => ({
          ...prev,
          selectedPiece: undefined,
          legalMoves: [],
          highlightedSquares: [],
          promotionPending: { from, to }
        }));
        return;
      }
    }

    // Start animation
    if (piece) {
      // Clear selection immediately to prevent re-selection during animation
      setState(prev => ({
        ...prev,
        selectedPiece: undefined,
        legalMoves: [],
        highlightedSquares: [],
        animatingMove: {
          from,
          to,
          piece,
          capturedPiece: capturedPiece || undefined
        },
        captureAnimation: capturedPiece ? { position: to, piece: capturedPiece } : undefined
      }));

      // Execute move after animation delay
      setTimeout(() => {
        const result = engine.makeMove(from, to);
        
        if (result.success && result.move) {
          // Clear animation and update last move
          setState(prev => ({
            ...prev,
            lastMove: result.move,
            animatingMove: undefined,
            captureAnimation: undefined
          }));

          // Notify parent component
          if (onMove) {
            onMove(result.move);
          }

          // Add haptic feedback for successful move
          if ('vibrate' in navigator) {
            navigator.vibrate(15);
          }
        } else {
          // Move failed - clear animation
          setState(prev => ({
            ...prev,
            animatingMove: undefined,
            captureAnimation: undefined
          }));
        }
      }, 300); // Match animation duration in CSS
    }
  }, [board, engine, onMove]);

  /**
   * Handles promotion piece selection
   */
  const handlePromotionSelect = useCallback((pieceType: 'queen' | 'rook' | 'bishop' | 'knight') => {
    if (!state.promotionPending) return;

    const { from, to } = state.promotionPending;
    const piece = board[from.rank][from.file];
    const capturedPiece = board[to.rank][to.file];

    // Start animation
    if (piece) {
      setState(prev => ({
        ...prev,
        promotionPending: undefined,
        animatingMove: {
          from,
          to,
          piece,
          capturedPiece: capturedPiece || undefined
        },
        captureAnimation: capturedPiece ? { position: to, piece: capturedPiece } : undefined
      }));

      // Execute move with promotion after animation delay
      setTimeout(() => {
        const result = engine.makeMove(from, to, pieceType);
        
        if (result.success && result.move) {
          // Clear animation and update last move
          setState(prev => ({
            ...prev,
            lastMove: result.move,
            animatingMove: undefined,
            captureAnimation: undefined
          }));

          // Notify parent component
          if (onMove) {
            onMove(result.move);
          }

          // Add haptic feedback for successful move
          if ('vibrate' in navigator) {
            navigator.vibrate(15);
          }
        } else {
          // Move failed - clear animation
          setState(prev => ({
            ...prev,
            animatingMove: undefined,
            captureAnimation: undefined
          }));
        }
      }, 300); // Match animation duration in CSS
    }
  }, [state.promotionPending, board, engine, onMove]);

  /**
   * Handles drag start for piece movement
   */
  const handleDragStart = useCallback((e: React.DragEvent, position: Position) => {
    if (!interactive) {
      e.preventDefault();
      return;
    }

    const piece = board[position.rank][position.file];
    if (!piece || piece.color !== currentPlayer) {
      e.preventDefault();
      return;
    }

    setDraggedPiece({ piece, from: position });
    selectPiece(position);

    // Set drag image
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
    }
  }, [interactive, board, currentPlayer, selectPiece]);

  /**
   * Handles drag over for valid drop targets
   */
  const handleDragOver = useCallback((e: React.DragEvent, position: Position) => {
    if (!draggedPiece) return;

    const isLegalMove = state.legalMoves.some(move => positionsEqual(move, position));
    if (isLegalMove) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
  }, [draggedPiece, state.legalMoves]);

  /**
   * Handles drop for piece movement
   */
  const handleDrop = useCallback((e: React.DragEvent, position: Position) => {
    e.preventDefault();
    
    if (!draggedPiece) return;

    const isLegalMove = state.legalMoves.some(move => positionsEqual(move, position));
    if (isLegalMove) {
      executeMove(draggedPiece.from, position);
    }

    setDraggedPiece(null);
  }, [draggedPiece, state.legalMoves, executeMove]);

  /**
   * Handles drag end
   */
  const handleDragEnd = useCallback(() => {
    setDraggedPiece(null);
  }, []);

  /**
   * Handles touch start for mobile drag-and-drop
   */
  const handleTouchStart = useCallback((e: React.TouchEvent, position: Position) => {
    if (!interactive) return;

    const piece = board[position.rank][position.file];
    if (!piece || piece.color !== currentPlayer) return;

    // Prevent default to avoid scrolling while dragging
    e.preventDefault();

    setTouchState({
      startPosition: position,
      currentPosition: position,
      isDragging: true
    });

    // Select the piece and show legal moves
    selectPiece(position);

    // Add haptic feedback if available
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  }, [interactive, board, currentPlayer, selectPiece]);

  /**
   * Handles touch move for mobile drag-and-drop
   */
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchState.isDragging || !touchState.startPosition) return;

    e.preventDefault();

    const touch = e.touches[0];
    if (!touch || !boardRef.current) return;

    // Get the element under the touch point
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!element) return;

    // Find the chess square element
    const square = element.closest('.chess-square') as HTMLElement;
    if (!square) return;

    // Get the square's position from data attribute
    const squareId = square.getAttribute('data-square');
    if (!squareId) return;

    // Parse position from square ID (e.g., "e4" -> {file: 4, rank: 3})
    const file = squareId.charCodeAt(0) - 'a'.charCodeAt(0);
    const rank = parseInt(squareId[1]) - 1;
    const position: Position = { file, rank };

    setTouchState(prev => ({
      ...prev,
      currentPosition: position
    }));
  }, [touchState.isDragging, touchState.startPosition]);

  /**
   * Handles touch end for mobile drag-and-drop
   */
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchState.isDragging || !touchState.startPosition) {
      setTouchState({ isDragging: false });
      return;
    }

    e.preventDefault();

    const touch = e.changedTouches[0];
    if (!touch || !boardRef.current) {
      setTouchState({ isDragging: false });
      return;
    }

    // Get the element under the touch point
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!element) {
      setTouchState({ isDragging: false });
      return;
    }

    // Find the chess square element
    const square = element.closest('.chess-square') as HTMLElement;
    if (!square) {
      setTouchState({ isDragging: false });
      return;
    }

    // Get the square's position from data attribute
    const squareId = square.getAttribute('data-square');
    if (!squareId) {
      setTouchState({ isDragging: false });
      return;
    }

    // Parse position from square ID
    const file = squareId.charCodeAt(0) - 'a'.charCodeAt(0);
    const rank = parseInt(squareId[1]) - 1;
    const endPosition: Position = { file, rank };

    // Check if this is a legal move
    const isLegalMove = state.legalMoves.some(move => positionsEqual(move, endPosition));

    if (isLegalMove) {
      // Execute the move
      executeMove(touchState.startPosition, endPosition);

      // Add haptic feedback for successful move
      if ('vibrate' in navigator) {
        navigator.vibrate(20);
      }
    } else if (positionsEqual(touchState.startPosition, endPosition)) {
      // Tapped the same piece - keep it selected (already selected from touchStart)
    } else {
      // Invalid move or tapped different square - deselect
      const piece = board[endPosition.rank][endPosition.file];
      if (piece && piece.color === currentPlayer) {
        // Tapped a different piece of the same color - select it
        selectPiece(endPosition);
      } else {
        // Tapped invalid square - deselect
        setState(prev => ({
          ...prev,
          selectedPiece: undefined,
          legalMoves: [],
          highlightedSquares: []
        }));
      }
    }

    setTouchState({ isDragging: false });
  }, [touchState, state.legalMoves, board, currentPlayer, executeMove, selectPiece]);

  /**
   * Handles touch cancel (e.g., interrupted by system gesture)
   */
  const handleTouchCancel = useCallback(() => {
    setTouchState({ isDragging: false });
  }, []);

  /**
   * Renders a chess piece using Unicode symbols
   */
  const renderPiece = (piece: ChessPiece | null): string => {
    if (!piece) return '';

    const pieceSymbols: Record<string, Record<string, string>> = {
      white: {
        king: '♔',
        queen: '♕',
        rook: '♖',
        bishop: '♗',
        knight: '♘',
        pawn: '♙'
      },
      black: {
        king: '♚',
        queen: '♛',
        rook: '♜',
        bishop: '♝',
        knight: '♞',
        pawn: '♟'
      }
    };

    return pieceSymbols[piece.color][piece.type] || '';
  };

  /**
   * Determines CSS classes for a square
   */
  const getSquareClasses = (rank: number, file: number): string => {
    const position: Position = { rank, file };
    const classes: string[] = ['chess-square'];

    // Alternating colors
    const isLight = (rank + file) % 2 === 0;
    classes.push(isLight ? 'chess-square--light' : 'chess-square--dark');

    // Selected piece
    if (state.selectedPiece && positionsEqual(state.selectedPiece, position)) {
      classes.push('chess-square--selected');
    }

    // Legal move highlight
    if (showLegalMoves && state.legalMoves.some(move => positionsEqual(move, position))) {
      const piece = board[rank][file];
      classes.push(piece ? 'chess-square--capture' : 'chess-square--legal');
    }

    // Last move highlight
    if (showLastMove && state.lastMove) {
      if (positionsEqual(state.lastMove.from, position) || positionsEqual(state.lastMove.to, position)) {
        classes.push('chess-square--last-move');
      }
    }

    // Check highlight
    if (gameStatus.type === 'check' || gameStatus.type === 'checkmate') {
      const piece = board[rank][file];
      if (piece && piece.type === 'king') {
        const kingInCheck = gameStatus.type === 'check' ? gameStatus.player : 
                           (gameStatus.type === 'checkmate' ? (gameStatus.winner === 'white' ? 'black' : 'white') : null);
        if (piece.color === kingInCheck) {
          classes.push(gameStatus.type === 'checkmate' ? 'chess-square--checkmate' : 'chess-square--check');
        }
      }
    }

    // Capture animation
    if (state.captureAnimation && positionsEqual(state.captureAnimation.position, position)) {
      classes.push('chess-square--capturing');
    }

    return classes.join(' ');
  };

  /**
   * Gets CSS classes for a piece including animation states
   */
  const getPieceClasses = (position: Position, piece: ChessPiece): string => {
    const classes: string[] = ['chess-piece', `chess-piece--${piece.color}`];

    // Check if this piece is being animated
    if (state.animatingMove && 
        positionsEqual(state.animatingMove.from, position)) {
      classes.push('chess-piece--moving');
    }

    // Check if this piece is being captured
    if (state.captureAnimation && 
        positionsEqual(state.captureAnimation.position, position)) {
      classes.push('chess-piece--captured');
    }

    return classes.join(' ');
  };

  /**
   * Renders the chess board
   */
  const renderBoard = () => {
    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    const ranks = [8, 7, 6, 5, 4, 3, 2, 1];

    // Adjust for orientation
    const displayRanks = orientation === 'white' ? ranks : [...ranks].reverse();
    const displayFiles = orientation === 'white' ? files : [...files].reverse();

    return (
      <div className="chess-board-container">
        <div className="chess-board" ref={boardRef}>
          <div className="chess-board__grid">
            {displayRanks.map((displayRank) => {
              const rank = displayRank - 1; // Convert to 0-indexed
              
              return displayFiles.map((displayFile, fileIndex) => {
                const file = files.indexOf(displayFile);
                const position: Position = { rank, file };
                const piece = board[rank][file];
                const squareId = `${displayFile}${displayRank}`;

                return (
                  <div
                    key={squareId}
                    className={getSquareClasses(rank, file)}
                    onClick={() => handleSquareClick(position)}
                    onDragOver={(e) => handleDragOver(e, position)}
                    onDrop={(e) => handleDrop(e, position)}
                    onTouchStart={(e) => handleTouchStart(e, position)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchCancel}
                    data-square={squareId}
                    role="button"
                    tabIndex={0}
                    aria-label={`Square ${squareId}${piece ? `, ${piece.color} ${piece.type}` : ', empty'}`}
                  >
                    {piece && (
                      <div
                        className={getPieceClasses(position, piece)}
                        draggable={interactive && piece.color === currentPlayer && !isAIThinking}
                        onDragStart={(e) => handleDragStart(e, position)}
                        onDragEnd={handleDragEnd}
                      >
                        {renderPiece(piece)}
                      </div>
                    )}
                    
                    {/* Coordinates */}
                    {showCoordinates && (
                      <>
                        {fileIndex === 0 && (
                          <span className="chess-coordinate chess-coordinate--rank">
                            {displayRank}
                          </span>
                        )}
                        {rank === (orientation === 'white' ? 0 : 7) && (
                          <span className="chess-coordinate chess-coordinate--file">
                            {displayFile}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                );
              });
            })}
          </div>

          {/* AI Thinking Indicator */}
          {isAIThinking && (
            <div className="chess-board__ai-thinking">
              <LoadingIndicator
                variant="dots"
                size="medium"
                color="primary"
                message="AI is thinking..."
              />
            </div>
          )}

          {/* Game Status Overlay */}
          {/* Game-over display handled by UniversalGameLayout overlay */}
        </div>
      </div>
    );
  };

  return (
    <>
      {renderBoard()}
      
      {/* Promotion Dialog */}
      <PromotionDialog
        color={currentPlayer}
        onSelect={handlePromotionSelect}
        isOpen={!!state.promotionPending}
      />
    </>
  );
};

export default ChessBoard;
